import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import type { RootState } from '../../redux/store';
import { useNavigationPermissions } from '../../hooks/useNavigationPermissions';
import { useDashboardLocation } from '../../contexts/DashboardLocationContext';

/** 05–11:59 días · 12–18:59 tardes · 19–04:59 noches (hora del dispositivo). */
const greetingForHour = (hour: number): string => {
  if (hour >= 5 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
};

const formatToday = (date: Date): string =>
  date.toLocaleDateString('es-BO', { weekday: 'long', day: 'numeric', month: 'short' });

/**
 * Inicio header for every role with access to Inicio: greeting + quick actions.
 * Each quick action only shows if the role can open its menu.
 */
export const HomeWelcome: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { locations } = useDashboardLocation();
  const { canInventoryGeneral } = useNavigationPermissions();

  const now = new Date();
  const locationName =
    user?.primaryLocationId != null
      ? locations.find((l) => l.id === user.primaryLocationId)?.name
      : undefined;
  const subtitle = [locationName, formatToday(now)].filter(Boolean).join(' · ');

  const quickActions = [
    canInventoryGeneral && {
      key: 'search-piece',
      label: 'Buscar pieza',
      icon: <SearchIcon />,
      path: '/dashboard/inventory/general',
    },
  ].filter(Boolean) as { key: string; label: string; icon: React.ReactNode; path: string }[];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        mb: 2,
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {greetingForHour(now.getHours())}
          {user?.firstName ? `, ${user.firstName}` : ''}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      </Box>
      {quickActions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {quickActions.map((a) => (
            <ButtonBase
              key={a.key}
              onClick={() => navigate(a.path)}
              sx={(t) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                color: t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.main,
                backgroundColor: alpha(
                  t.palette.mode === 'dark' ? t.palette.common.white : t.palette.primary.main,
                  t.palette.mode === 'dark' ? 0.08 : 0.08,
                ),
                transition:
                  'background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                '& svg': { fontSize: 18 },
                '&:hover': {
                  backgroundColor: alpha(
                    t.palette.mode === 'dark' ? t.palette.common.white : t.palette.primary.main,
                    t.palette.mode === 'dark' ? 0.14 : 0.14,
                  ),
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(t.palette.primary.main, 0.18)}`,
                },
              })}
            >
              {a.icon}
              {a.label}
            </ButtonBase>
          ))}
        </Box>
      )}
    </Box>
  );
};
